/**
 * @file PointerCapability.ts
 * @role Reports whether the device's primary pointer is coarse (finger) or fine (mouse).
 *       Lives in layout infrastructure because it reads a viewport media query, which
 *       scripts/check-repo-policy.mjs confines to src/core/layout/.
 * @owns isCoarsePointer().
 * @reads An injectable matchMedia seam (defaults to window; absent in non-browser envs).
 * @neverDoes Classify orientation or size — LayoutService owns those and publishes them
 *            through LayoutContext.
 * @see src/core/layout/LayoutService.ts, docs/UI_LAYOUT_GUIDE.md
 */

export interface PointerCapabilityWindow {
  matchMedia?: (query: string) => { matches: boolean };
}

function defaultWindow(): PointerCapabilityWindow | undefined {
  return typeof window === 'undefined' ? undefined : window;
}

/**
 * True on phones and tablets, false on mouse-driven desktops.
 *
 * Deliberately a capability check rather than a width check: a desktop browser resized narrow
 * is still a desktop, and a narrow viewport is not consent to take over the screen.
 */
export function isCoarsePointer(
  win: PointerCapabilityWindow | undefined = defaultWindow(),
): boolean {
  return win?.matchMedia?.('(pointer: coarse)').matches === true;
}
